import React, { Children } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/ui/Footer';
import { AlertCircle, CheckCircle, TrendingUp, Users, DollarSign, Target } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.6
  }
};
const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function WhatIsMDMPage() {
  return <div className="bg-black min-h-screen" data-id="element-357">
      <Header data-id="element-358" />
      <main className="pt-32 pb-24 px-6" data-id="element-359">
        <div className="max-w-4xl mx-auto" data-id="element-360">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="mb-16 text-center" data-id="element-361">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-6" data-id="element-362">
              What Is MDM?
            </h1>
            <p className="text-xl text-white/80 leading-relaxed" data-id="element-363">
              (And Why It Could Be the Biggest Growth Lever You Haven't Pulled
              Yet)
            </p>
          </motion.div>
          <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-12" data-id="element-364">
            <motion.section variants={fadeInUp} className="bg-white/5 border border-white/10 rounded-2xl p-8" data-id="element-365">
              <h2 className="text-3xl font-semibold text-white mb-6" data-id="element-366">
                The Definition (Simplified)
              </h2>
              <p className="text-white/80 leading-relaxed mb-6" data-id="element-367">
                Master Data Management (MDM) is the process of making sure
                there's one clean, accurate version of your most important data
                — the data about your customers, suppliers, locations,
                clinicians, products, and accounts — and making sure every
                system uses it.
              </p>
              <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-xl p-6" data-id="element-368">
                <p className="text-white/80 leading-relaxed mb-4 font-semibold" data-id="element-369">
                  Think of it like organizing building blocks:
                </p>
                <ul className="space-y-3 text-white/70" data-id="element-370">
                  <li className="flex items-start gap-2" data-id="element-371">
                    <span className="text-purple-400 mt-1" data-id="element-372">•</span>
                    <span data-id="element-373">
                      Right now, your data is scattered across systems,
                      unlabeled, and inconsistent.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-374">
                    <span className="text-purple-400 mt-1" data-id="element-375">•</span>
                    <span data-id="element-376">
                      MDM sorts the pieces, labels them correctly, and organizes
                      them systematically.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-377">
                    <span className="text-purple-400 mt-1" data-id="element-378">•</span>
                    <span data-id="element-379">
                      Suddenly, everyone can work from the same accurate
                      information without conflicts.
                    </span>
                  </li>
                </ul>
              </div>
            </motion.section>
            <motion.section variants={fadeInUp} className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-white/10 rounded-2xl p-8" data-id="element-380">
              <div className="flex items-start gap-4 mb-6" data-id="element-381">
                <AlertCircle className="w-8 h-8 text-red-400 flex-shrink-0" data-id="element-382" />
                <h2 className="text-3xl font-semibold text-white" data-id="element-383">
                  The Problem: Your Business Doesn't Agree on What's True
                </h2>
              </div>
              <p className="text-white/80 leading-relaxed mb-6" data-id="element-384">
                Right now, every department in your organization has its own
                version of reality.
              </p>
              <ul className="space-y-3 text-white/70" data-id="element-385">
                <li className="flex items-start gap-2" data-id="element-386">
                  <span className="text-red-400 mt-1" data-id="element-387">•</span>
                  <span data-id="element-388">Sales has one number for customers.</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-389">
                  <span className="text-red-400 mt-1" data-id="element-390">•</span>
                  <span data-id="element-391">Finance has another.</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-392">
                  <span className="text-red-400 mt-1" data-id="element-393">•</span>
                  <span data-id="element-394">Operations has a third.</span>
                </li>
              </ul>
              <p className="text-white/80 leading-relaxed mt-6 mb-4" data-id="element-395">
                Which one is right? Which one gets used for decisions?
              </p>
              <p className="text-white/80 leading-relaxed mb-4" data-id="element-396">
                This disagreement shows up as:
              </p>
              <ul className="space-y-2 text-white/70" data-id="element-397">
                <li className="flex items-start gap-2" data-id="element-398">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-399" />
                  <span data-id="element-400">Duplicate records that cost you money</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-401">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-402" />
                  <span data-id="element-403">Write-offs and rework</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-404">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-405" />
                  <span data-id="element-406">Delayed projects</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-407">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-408" />
                  <span data-id="element-409">Meetings that turn into arguments</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-410">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-411" />
                  <span data-id="element-412">Bad forecasts and missed revenue targets</span>
                </li>
              </ul>
              <p className="text-white/80 leading-relaxed mt-6 font-semibold" data-id="element-413">
                That's the MDM problem in a nutshell — and it's costing you
                millions.
              </p>
            </motion.section>
            <motion.section variants={fadeInUp} className="bg-white/5 border border-white/10 rounded-2xl p-8" data-id="element-414">
              <h2 className="text-3xl font-semibold text-white mb-6" data-id="element-415">
                The Payoff: What You Get When You Get It Right
              </h2>
              <p className="text-white/80 leading-relaxed mb-6" data-id="element-416">
                When you implement MDM correctly, you unlock:
              </p>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-417">
                {[{
                icon: CheckCircle,
                title: 'Cleaner Numbers',
                desc: 'Every system agrees on who your customers, providers, and suppliers are.'
              }, {
                icon: TrendingUp,
                title: 'Faster Decisions',
                desc: 'Leadership meetings shift from arguing about the data to acting on it.'
              }, {
                icon: DollarSign,
                title: 'Lower Costs',
                desc: 'Duplicate spend, rework, and billing errors disappear.'
              }, {
                icon: Users,
                title: 'Better Patient Care',
                desc: 'Clinicians have the right information, at the right time.'
              }, {
                icon: Target,
                title: 'Stronger M&A Outcomes',
                desc: 'Integrations go smoother because your data is already clean and mapped.'
              }, {
                icon: TrendingUp,
                title: 'AI-Readiness',
                desc: 'Your data becomes machine-readable, enabling automation and predictive insights.'
              }].map((item, index) => <div key={index} className="bg-black/30 border border-white/10 rounded-xl p-6" data-id="element-418">
                    <div className="flex items-center gap-3 mb-3" data-id="element-419">
                      <item.icon className="w-6 h-6 text-purple-400" data-id="element-420" />
                      <h3 className="text-lg font-semibold text-white" data-id="element-421">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-white/70 text-sm" data-id="element-422">{item.desc}</p>
                  </div>)}
              </div>
            </motion.section>
            <motion.section variants={fadeInUp} className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8" data-id="element-423">
              <div className="flex items-start gap-4 mb-6" data-id="element-424">
                <TrendingUp className="w-8 h-8 text-cyan-400 flex-shrink-0" data-id="element-425" />
                <h2 className="text-3xl font-semibold text-white" data-id="element-426">
                  Why Billion-Dollar Companies Obsess Over This
                </h2>
              </div>
              <p className="text-white/80 leading-relaxed mb-4" data-id="element-427">
                Fortune 500 companies like UnitedHealth, John Deere, and
                American Airlines spend millions to get this right — because
                they know:
              </p>
              <ul className="space-y-3 text-white/70 mb-6" data-id="element-428">
                <li className="flex items-start gap-2" data-id="element-429">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" data-id="element-430" />
                  <span data-id="element-431">
                    Clean data is the foundation for AI, automation, and
                    accurate reporting.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-432">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" data-id="element-433" />
                  <span data-id="element-434">
                    When your data is wrong, your forecasts, budgets, and
                    decisions are wrong too.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-435">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" data-id="element-436" />
                  <span data-id="element-437">
                    Getting it right creates a flywheel of efficiency: faster
                    decisions, better outcomes, and higher profitability.
                  </span>
                </li>
              </ul>
              <p className="text-white/80 leading-relaxed font-semibold" data-id="element-438">
                Mid-market companies can get the same competitive advantage —
                but most haven't pulled this lever yet.
              </p>
            </motion.section>
            <motion.section variants={fadeInUp} className="bg-white/5 border border-white/10 rounded-2xl p-8" data-id="element-439">
              <h2 className="text-3xl font-semibold text-white mb-6" data-id="element-440">
                Why This Is a Seven-Figure Problem
              </h2>
              <p className="text-white/80 leading-relaxed mb-4 font-semibold" data-id="element-441">
                The "misalignment tax" is real.
              </p>
              <ul className="space-y-3 text-white/70 mb-6" data-id="element-442">
                <li className="flex items-start gap-2" data-id="element-443">
                  <span className="text-orange-400 mt-1" data-id="element-444">•</span>
                  <span data-id="element-445">
                    Every duplicate provider or customer record creates billing
                    errors.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-446">
                  <span className="text-orange-400 mt-1" data-id="element-447">•</span>
                  <span data-id="element-448">
                    Every disagreement between finance and ops delays decisions.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-449">
                  <span className="text-orange-400 mt-1" data-id="element-450">•</span>
                  <span data-id="element-451">
                    Every bad forecast leads to overstaffing or stockouts.
                  </span>
                </li>
              </ul>
              <p className="text-white/80 leading-relaxed font-semibold" data-id="element-452">
                When we perform an MDM audit, we almost always find seven
                figures in hidden losses — sometimes eight.
              </p>
            </motion.section>
            <motion.section variants={fadeInUp} className="bg-white/5 border border-white/10 rounded-2xl p-8" data-id="element-453">
              <h2 className="text-3xl font-semibold text-white mb-6" data-id="element-454">
                Why Most MDM Projects Fail (and How We're Different)
              </h2>
              <p className="text-white/80 leading-relaxed mb-4" data-id="element-455">
                Here's the truth: most MDM efforts crash and burn. Why?
              </p>
              <ul className="space-y-3 text-white/70 mb-6" data-id="element-456">
                <li className="flex items-start gap-2" data-id="element-457">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-458" />
                  <span data-id="element-459">
                    They stay stuck in PowerPoints — never implemented.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-460">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-461" />
                  <span data-id="element-462">
                    They're too technical and never get business buy-in.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-463">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-464" />
                  <span data-id="element-465">
                    They're treated as IT projects instead of business strategy.
                  </span>
                </li>
              </ul>
              <p className="text-white/80 leading-relaxed mb-4 font-semibold" data-id="element-466">
                We fix that by running MDM as a business-first initiative:
              </p>
              <div className="space-y-4" data-id="element-467">
                <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-xl p-6" data-id="element-468">
                  <h3 className="text-lg font-semibold text-white mb-2" data-id="element-469">
                    Find the Money
                  </h3>
                  <p className="text-white/70" data-id="element-470">
                    Quantify the leaks first — show hard-dollar impact to the
                    CFO.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-xl p-6" data-id="element-471">
                  <h3 className="text-lg font-semibold text-white mb-2" data-id="element-472">
                    Build the Blueprint
                  </h3>
                  <p className="text-white/70" data-id="element-473">
                    Align executives, define the "golden records," and set the
                    strategy.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-xl p-6" data-id="element-474">
                  <h3 className="text-lg font-semibold text-white mb-2" data-id="element-475">
                    Do the Heavy Lifting
                  </h3>
                  <p className="text-white/70" data-id="element-476">
                    Our team rolls up sleeves and implements end-to-end — your
                    people keep running the business.
                  </p>
                </div>
              </div>
            </motion.section>
            <motion.section variants={fadeInUp} className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8" data-id="element-477">
              <h2 className="text-3xl font-semibold text-white mb-6" data-id="element-478">
                Who We Are
              </h2>
              <p className="text-white/80 leading-relaxed mb-4" data-id="element-479">
                ProviderMDM is led by Brigitte Tebow, one of the most
                sought-after MDM & Data Governance experts in the U.S., trusted
                by health systems, payers, and government agencies.
              </p>
              <p className="text-white/80 leading-relaxed mb-4" data-id="element-480">
                She's implemented MDM programs at:
              </p>
              <div className="grid md:grid-cols-2 gap-3 mb-6" data-id="element-481">
                {['Harvard Medical School', 'Yale University School of Medicine', 'UnitedHealth Group', 'Optum', 'Mass General Brigham', 'Kaiser Permanente', 'Blue Cross Blue Shield Association'].map((org, index) => <div key={index} className="flex items-center gap-2 text-white/70" data-id="element-482">
                    <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0" data-id="element-483" />
                    <span data-id="element-484">{org}</span>
                  </div>)}
              </div>
              <p className="text-white/80 leading-relaxed font-semibold" data-id="element-485">
                With a proven track record of successful implementations.
              </p>
            </motion.section>
            <motion.section variants={fadeInUp} className="bg-black border border-white/10 rounded-2xl p-8 lg:p-12 text-center" data-id="element-486">
              <h2 className="text-3xl font-semibold text-white mb-6" data-id="element-487">
                Your Next Step
              </h2>
              <p className="text-white/80 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-488">
                If you're ready to stop paying the misalignment tax, the next
                step is simple:
              </p>
              <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-xl p-8 mb-8" data-id="element-489">
                <h3 className="text-2xl font-semibold text-white mb-4" data-id="element-490">
                  Book a 15-Minute Executive Consult
                </h3>
                <p className="text-white/70 leading-relaxed mb-6" data-id="element-491">
                  We'll find out in minutes whether MDM is your biggest unlock —
                  and you'll walk away with clarity (and at least one insight
                  you can act on right away).
                </p>
                <motion.div className="inline-block" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} data-id="element-492">
                  <Link to="/contact" className="inline-flex items-center justify-center h-12 rounded-full px-8 text-sm bg-white text-black font-semibold hover:bg-white/90 transition-colors" data-id="element-493">
                    Book a Consult
                  </Link>
                </motion.div>
              </div>
            </motion.section>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-494" />
    </div>;
}